// src/services/userRolePermissionService.js
import apiService from '../utils/apiInterceptor';

const userRolePermissionService = {
  /**
   * Get user roles by user ID
   * @param {number|string} userId - User ID
   * @returns {Promise<Object>} Promise resolving to user roles data
   */
  getUserRoles: async (userId) => {
    try {
      const response = await apiService.get(`/admin/user/${userId}/roles`);
      
      // Store the full roles data including is_admin flag
      if (response.data && response.data.roles) {
        localStorage.setItem('user_roles_full', JSON.stringify(response.data.roles));
      }
      
      return response;
    } catch (error) {
      console.error(`Error fetching roles for user with ID ${userId}:`, error);
      throw error;
    }
  },

  /**
   * Assign roles to user
   * @param {number|string} userId - User ID
   * @param {Array} roleIds - Array of role IDs
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  assignUserRoles: async (userId, roleIds) => {
    try {
      return await apiService.post(`/admin/user/${userId}/roles`, { roles: roleIds });
    } catch (error) {
      console.error(`Error assigning roles to user with ID ${userId}:`, error);
      throw error;
    }
  },

  /**
   * Get user permissions by user ID
   * @param {number|string} userId - User ID
   * @returns {Promise<Object>} Promise resolving to user permissions data
   */
  getUserPermissions: async (userId) => {
    try {
      return await apiService.get(`/admin/user/${userId}/permissions`);
    } catch (error) {
      console.error(`Error fetching permissions for user with ID ${userId}:`, error);
      throw error;
    }
  },
  
  /**
   * Check if user has specific permission
   * @param {string} permissionName - Permission name to check
   * @returns {boolean} True if user has permission, false otherwise
   */
  hasPermission: (permissionName) => {
    // Get user permissions from localStorage
    const userPermissions = JSON.parse(localStorage.getItem('user_permissions') || '[]');
    
    // Get the user roles to check for admin status
    const userRolesFull = JSON.parse(localStorage.getItem('user_roles_full') || '[]');
    const isAdmin = userRolesFull.some(role => role.is_admin === 1);
    
    // Admin users have all permissions
    if (isAdmin) {
      return true;
    }
    
    return userPermissions.includes(permissionName);
  },
  
  /**
   * Check if user has any of the specified permissions
   * @param {Array} permissionNames - Array of permission names to check
   * @returns {boolean} True if user has any of the permissions, false otherwise
   */
  hasAnyPermission: (permissionNames) => {
    // Get the user roles to check for admin status
    const userRolesFull = JSON.parse(localStorage.getItem('user_roles_full') || '[]');
    const isAdmin = userRolesFull.some(role => role.is_admin === 1);
    
    // Admin users have all permissions
    if (isAdmin) {
      return true;
    }
    
    const userPermissions = JSON.parse(localStorage.getItem('user_permissions') || '[]');
    return permissionNames.some(permission => userPermissions.includes(permission));
  },
  
  /**
   * Check if user has all of the specified permissions
   * @param {Array} permissionNames - Array of permission names to check
   * @returns {boolean} True if user has all of the permissions, false otherwise
   */
  hasAllPermissions: (permissionNames) => {
    // Get the user roles to check for admin status
    const userRolesFull = JSON.parse(localStorage.getItem('user_roles_full') || '[]');
    const isAdmin = userRolesFull.some(role => role.is_admin === 1);
    
    // Admin users have all permissions
    if (isAdmin) {
      return true;
    }
    
    const userPermissions = JSON.parse(localStorage.getItem('user_permissions') || '[]');
    return permissionNames.every(permission => userPermissions.includes(permission));
  },
  
  /**
   * Load user permissions into localStorage
   * @param {number|string} userId - User ID
   */
  loadUserPermissions: async (userId) => {
    try {
      const response = await userRolePermissionService.getUserPermissions(userId);
      
      if (response.data && response.data.permissions) {
        // Extract permission names to simple array
        const permissionNames = response.data.permissions.map(p => p.name);
        localStorage.setItem('user_permissions', JSON.stringify(permissionNames));
        
        // Store full permissions data including is_admin flag
        localStorage.setItem('user_permissions_full', JSON.stringify(response.data.permissions));
      }
    } catch (error) {
      console.error('Error loading user permissions:', error);
      // Set empty permissions array on error
      localStorage.setItem('user_permissions', '[]');
      localStorage.setItem('user_permissions_full', '[]');
    }
  },
  
  /**
   * Check if a user has any admin roles
   * @returns {boolean} True if user has any admin roles
   */
  hasAdminRole: () => {
    const userRolesFull = JSON.parse(localStorage.getItem('user_roles_full') || '[]');
    return userRolesFull.some(role => role.is_admin === 1);
  },
  
  /**
   * Get all admin-only permissions
   * @returns {Array} Array of admin permission names
   */
  getAdminPermissions: () => {
    const userPermissionsFull = JSON.parse(localStorage.getItem('user_permissions_full') || '[]');
    return userPermissionsFull
      .filter(permission => permission.is_admin === 1)
      .map(permission => permission.name);
  },
  
  /**
   * Get all regular (non-admin) permissions
   * @returns {Array} Array of regular permission names
   */
  getRegularPermissions: () => {
    const userPermissionsFull = JSON.parse(localStorage.getItem('user_permissions_full') || '[]');
    return userPermissionsFull
      .filter(permission => permission.is_admin !== 1)
      .map(permission => permission.name);
  }
};

export default userRolePermissionService;